import type { BehaviorMetric } from '../../../gateways/breadstack/customer/behaviorOverviewApis'
import type { PillTone } from '../components/profile/tabs/behavior/v2/shared/theme'
import { type MetricDelta, formatInt } from './behaviorOverview'

// Pure display helpers for the Behavior › Site Search sub-tab. Free of React
// so edge cases are unit-tested.

// Count KPIs (Search Terms Used, Zero Results) show an ABSOLUTE change —
// `current − previous` — like the mockup's "▲ 3" / "▼ 1", not a %. Both
// numbers always exist, so this never returns undefined. `inverted` flips the
// colour sense for lower-is-better counts (Zero Results).
export const metricAbsDelta = (
  metric: BehaviorMetric,
  { inverted = false }: { inverted?: boolean } = {},
): MetricDelta => {
  const diff = (metric?.current ?? 0) - (metric?.previous ?? 0)
  if (!Number.isFinite(diff) || diff === 0) return { text: '0', dir: 'flat' }
  const text = formatInt(Math.abs(diff))
  if (diff > 0) return { text, dir: inverted ? 'invUp' : 'up' }
  return { text, dir: inverted ? 'invDown' : 'down' }
}

const whole = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

const cents = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

// Always 2 decimals: `$78.40`, `$112.00`. Non-finite → `$0.00`. (Cart &
// Checkout money; shares the `cents` formatter with `formatMoneySmart`.)
export const formatMoney2 = (value: number | null | undefined): string =>
  cents.format(typeof value === 'number' && Number.isFinite(value) ? value : 0)

// `$1,480` for whole-dollar amounts, `$68.50` otherwise (mockup Search Revenue
// vs AOV). Non-finite → `$0`.
export const formatMoneySmart = (value: number | null | undefined): string => {
  const v = typeof value === 'number' && Number.isFinite(value) ? value : 0
  const rounded = Math.round(v * 100) / 100
  return Number.isInteger(rounded)
    ? whole.format(rounded)
    : cents.format(rounded)
}

// Term-table conversion pill: green ≥ 50, amber > 0, red at 0.
export const convPillTone = (rate: number): PillTone => {
  if (Number.isFinite(rate) && rate >= 50) return 'green'
  if (Number.isFinite(rate) && rate > 0) return 'amber'
  return 'red'
}

// Pill text as in the mockup: `61.1%`, `50.0%`, but whole `0%` / `100%`.
export const formatConvRate = (rate: number): string => {
  const v = Number.isFinite(rate) ? Math.min(100, Math.max(0, rate)) : 0
  if (v === 0 || v === 100) return `${v}%`
  return `${v.toFixed(1)}%`
}
